// Copyright (c) 2023 WiseTime. All rights reserved.

import { notUndefined } from "../../../lib/utils"
import { difference, uniqBy } from "ramda"

// An indexer that manages the state for a cache of items, indexed by a key.
// The state is mutated in place as this indexer is expected to be used with the Immer library.
export type Indexer<K, T, State> = {
  upsert: (item: T, state: State) => void,
  updateInCache: (item: T, state: State) => void,
  remove: (id: Id, state: State) => void,
  replaceForIndex: (name: string, key: K, items: T[], state: State) => void,
  findById: (id: Id, state: State) => T | undefined,
  findByIndex: (name: string, searchKey: K, state: State) => T[],
  all: (state: State) => T[],
  cache: (state: State) => Cache<T>,
}

export type Id = string

export type Cache<T> = {
  [id: Id]: T,
}

type IndexKey = string | number
export type Index<K extends IndexKey> = Record<K, Id[]>

export type IndexerConfig<K extends IndexKey, T, State> = {
  selectCache: (state: State) => Cache<T>,
  selectIndexes: IndexSelector<K, T, State>[], // TODO: Support different index key types for indexes.
  getId: (item: T) => Id,
  onItemUpserted?: (item: T, resultingState: State) => void,
  onItemsRemoved?: (items: T[], resultingState: State) => void,
}

type IndexSelector<K extends IndexKey, T, State> = {
  name: string,
  state: (state: State) => Index<K>,
  filter?: (item: T) => boolean,
  extractKey: (item: T) => K,
}

const getIndexFilter = <K extends IndexKey, T, State>(index: IndexSelector<K, T, State>): (item: T) => boolean =>
  index.filter !== undefined ? index.filter : (_: T) => true

const removeFromIndexes = <K extends IndexKey, T, State>(config: IndexerConfig<K, T, State>) =>
  (item: T, state: State): void => {
    const id = config.getId(item)

    config.selectIndexes.forEach(index => {
      const key = index.extractKey(item)
      const indexState = index.state(state)
      const position = (indexState[key] || []).findIndex(v => v === id)
      if (position !== -1) {
        indexState[key]?.splice(position, 1)
      }
      if (indexState[key]?.length === 0) {
        delete indexState[key]
      }
    })
  }

const upsertItem = <K extends IndexKey, T, State>(config: IndexerConfig<K, T, State>) =>
  (item: T, state: State): void => {
    const id = config.getId(item)
    const cache = config.selectCache(state)

    // Update indexes.
    const existingItem = cache[id]
    if (existingItem !== undefined) {
      removeFromIndexes(config)(existingItem, state)
    }
    config.selectIndexes.forEach(index => {
      const indexFilter = getIndexFilter(index)

      if (indexFilter(item)) {
        const indexState = index.state(state)
        const itemKey = index.extractKey(item)
        const indexedKey = indexState[itemKey]
        if (indexedKey === undefined) {
          indexState[itemKey] = [id]
        } else {
          indexedKey.push(id)
        }
      }
    })
    // Update cache.
    cache[id] = item

    // Notify.
    if (config.onItemUpserted !== undefined) {
      config.onItemUpserted(item, state)
    }
  }

// Update item in cache only, without re-indexing. Use when index keys haven't changed.
const updateItemInCache = <K extends IndexKey, T, State>(config: IndexerConfig<K, T, State>) =>
  (item: T, state: State): void => {
    const id = config.getId(item)
    const cache = config.selectCache(state)
    if (cache[id] !== undefined) {
      cache[id] = item
      if (config.onItemUpserted !== undefined) {
        config.onItemUpserted(item, state)
      }
    }
  }

const removeItem = <K extends IndexKey, T, State>(config: IndexerConfig<K, T, State>) =>
  (id: Id, state: State): void => {
    const cache = config.selectCache(state)
    const item = cache[id]
    if (!item) {
      return
    }
    removeFromIndexes(config)(item, state)
    delete cache[id]
    if (config.onItemsRemoved !== undefined) {
      config.onItemsRemoved([item], state)
    }
  }

const findIndexMemberships = <K extends IndexKey, T, State>(config: IndexerConfig<K, T, State>) =>
  (item: T, state: State): IndexSelector<K, T, State>[] =>
    config.selectIndexes.filter(index => {
      const key = index.extractKey(item)
      const itemId = config.getId(item)
      return index.state(state)[key]?.find(id => id === itemId) !== undefined
    })

const isIndexedElsewhere = <K extends IndexKey, T, State>(config: IndexerConfig<K, T, State>) =>
  (exceptBy: string, item: T, state: State): boolean =>
    findIndexMemberships(config)(item, state)
      .filter(index => index.name !== exceptBy)
      .length > 0

const findStaleItems = <K extends IndexKey, T, State>(config: IndexerConfig<K, T, State>) =>
  (indexName: string, key: K, freshItems: T[], state: State): T[] => {
    const index = config.selectIndexes.find(index => index.name === indexName)
    if (index === undefined) {
      throw new Error(`Index ${indexName} not found.`)
    }

    const indexedIds = index.state(state)[key]
      ?.map(id => findItemById(config)(id, state))
      .filter(notUndefined)
      .map(item => config.getId(item)) || []

    const freshIds = freshItems.map(item => config.getId(item))
    const cache = config.selectCache(state)

    return difference(indexedIds, freshIds)
      .map(staleId => cache[staleId])
      .filter(notUndefined)
      .filter(item => !isIndexedElsewhere(config)(indexName, item, state))
  }

const replaceForIndex = <K extends IndexKey, T, State>(config: IndexerConfig<K, T, State>) =>
  (name: string, key: K, items: T[], state: State): void => {
    items.forEach(item => upsertItem(config)(item, state))

    const staleItems = findStaleItems(config)(name, key, items, state)
    staleItems.forEach(item => {
      const id = config.getId(item)
      removeItem(config)(id, state)
    })
    if (staleItems.length > 0 && config.onItemsRemoved !== undefined) {
      config.onItemsRemoved(staleItems, state)
    }
  }

const findItemById = <K extends IndexKey, T, State>(config: IndexerConfig<K, T, State>) =>
  (id: Id, state: State): T | undefined =>
    config.selectCache(state)[id]

const findItemsByIndex = <K extends IndexKey, T, State>(config: IndexerConfig<K, T, State>) =>
  (name: string, searchKey: K, state: State): T[] =>
    config.selectIndexes
      .filter(index => index.name === name)
      .flatMap(index => {
        const indexState = index.state(state)
        const cache = config.selectCache(state)
        const indexFilter = getIndexFilter(index)
        return indexState[searchKey]
          ?.map(id => cache[id])
          .filter(notUndefined)
          .filter(indexFilter) || []
      })

const cache = <K extends IndexKey, T, State>(config: IndexerConfig<K, T, State>) => (state: State): Cache<T> =>
  config.selectCache(state)

const all = <K extends IndexKey, T, State>(config: IndexerConfig<K, T, State>) => (state: State): T[] =>
  Object.values(config.selectCache(state))

export const createIndexer = <K extends IndexKey, T, State>(config: IndexerConfig<K, T, State>): Indexer<K, T, State> => {
  const allIndexes = config.selectIndexes
  const uniqueIndexNames = uniqBy(index => index.name, config.selectIndexes)
  if (uniqueIndexNames.length !== allIndexes.length) {
    throw new Error("Indexes must have unique names.")
  }
  return {
    upsert: upsertItem(config),
    updateInCache: updateItemInCache(config),
    remove: removeItem(config),
    replaceForIndex: replaceForIndex(config),
    findById: findItemById(config),
    findByIndex: findItemsByIndex(config),
    all: all(config),
    cache: cache(config),
  }
}
